/**
 * Error Handler Utility
 * نظام موحد لمعالجة الأخطاء في التطبيق
 * Unified error handling system for the application
 */

import { Alert, I18nManager } from 'react-native';
import { AxiosError } from 'axios';
import logger from './logger';

// أنواع الأخطاء المعروفة
export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTH = 'AUTH',
  VALIDATION = 'VALIDATION',
  SERVER = 'SERVER',
  TIMEOUT = 'TIMEOUT',
  NOT_FOUND = 'NOT_FOUND',
  PERMISSION = 'PERMISSION',
  UNKNOWN = 'UNKNOWN',
}

// واجهة الخطأ الموحدة
export interface AppError {
  type: ErrorType;
  message: string;
  messageAr: string;
  code?: string;
  statusCode?: number;
  originalError?: Error | AxiosError;
}

// رسائل الأخطاء المترجمة
const errorMessages: Record<ErrorType, { en: string; ar: string }> = {
  [ErrorType.NETWORK]: {
    en: 'Network connection error. Please check your internet connection.',
    ar: 'خطأ في الاتصال بالشبكة. يرجى التحقق من اتصالك بالإنترنت.',
  },
  [ErrorType.AUTH]: {
    en: 'Authentication error. Please login again.',
    ar: 'خطأ في المصادقة. يرجى تسجيل الدخول مرة أخرى.',
  },
  [ErrorType.VALIDATION]: {
    en: 'Invalid data. Please check your input.',
    ar: 'بيانات غير صالحة. يرجى التحقق من المدخلات.',
  },
  [ErrorType.SERVER]: {
    en: 'Server error. Please try again later.',
    ar: 'خطأ في الخادم. يرجى المحاولة لاحقاً.',
  },
  [ErrorType.TIMEOUT]: {
    en: 'Request timed out. Please try again.',
    ar: 'انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.',
  },
  [ErrorType.NOT_FOUND]: {
    en: 'The requested resource was not found.',
    ar: 'المورد المطلوب غير موجود.',
  },
  [ErrorType.PERMISSION]: {
    en: 'You do not have permission to perform this action.',
    ar: 'ليس لديك صلاحية لتنفيذ هذا الإجراء.',
  },
  [ErrorType.UNKNOWN]: {
    en: 'An unexpected error occurred. Please try again.',
    ar: 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.',
  },
};

/**
 * تحليل الخطأ وتحويله لخطأ موحد
 * Parse error and convert to unified AppError
 */
export function parseError(error: unknown): AppError {
  // خطأ Axios
  if (error instanceof AxiosError) {
    return parseAxiosError(error);
  }

  // خطأ عادي
  if (error instanceof Error) {
    return {
      type: ErrorType.UNKNOWN,
      message: error.message || errorMessages[ErrorType.UNKNOWN].en,
      messageAr: errorMessages[ErrorType.UNKNOWN].ar,
      originalError: error,
    };
  }

  // خطأ غير معروف
  return {
    type: ErrorType.UNKNOWN,
    message: typeof error === 'string' ? error : errorMessages[ErrorType.UNKNOWN].en,
    messageAr: errorMessages[ErrorType.UNKNOWN].ar,
  };
}

/**
 * تحليل خطأ Axios
 */
function parseAxiosError(error: AxiosError): AppError {
  // خطأ شبكة (لا يوجد استجابة)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return {
        type: ErrorType.TIMEOUT,
        message: errorMessages[ErrorType.TIMEOUT].en,
        messageAr: errorMessages[ErrorType.TIMEOUT].ar,
        code: error.code,
        originalError: error,
      };
    }
    return {
      type: ErrorType.NETWORK,
      message: errorMessages[ErrorType.NETWORK].en,
      messageAr: errorMessages[ErrorType.NETWORK].ar,
      code: error.code,
      originalError: error,
    };
  }

  const statusCode = error.response.status;
  const responseData = error.response.data as any;
  const serverMessage = responseData?.message || responseData?.error;

  // تحديد نوع الخطأ حسب كود الاستجابة
  switch (statusCode) {
    case 400:
      return {
        type: ErrorType.VALIDATION,
        message: serverMessage || errorMessages[ErrorType.VALIDATION].en,
        messageAr: responseData?.messageAr || errorMessages[ErrorType.VALIDATION].ar,
        statusCode,
        code: responseData?.code,
        originalError: error,
      };

    case 401:
      return {
        type: ErrorType.AUTH,
        message: serverMessage || errorMessages[ErrorType.AUTH].en,
        messageAr: responseData?.messageAr || errorMessages[ErrorType.AUTH].ar,
        statusCode,
        originalError: error,
      };

    case 403:
      return {
        type: ErrorType.PERMISSION,
        message: serverMessage || errorMessages[ErrorType.PERMISSION].en,
        messageAr: responseData?.messageAr || errorMessages[ErrorType.PERMISSION].ar,
        statusCode,
        originalError: error,
      };

    case 404:
      return {
        type: ErrorType.NOT_FOUND,
        message: serverMessage || errorMessages[ErrorType.NOT_FOUND].en,
        messageAr: responseData?.messageAr || errorMessages[ErrorType.NOT_FOUND].ar,
        statusCode,
        originalError: error,
      };

    case 500:
    case 502:
    case 503:
    case 504:
      return {
        type: ErrorType.SERVER,
        message: serverMessage || errorMessages[ErrorType.SERVER].en,
        messageAr: responseData?.messageAr || errorMessages[ErrorType.SERVER].ar,
        statusCode,
        originalError: error,
      };

    default:
      return {
        type: ErrorType.UNKNOWN,
        message: serverMessage || errorMessages[ErrorType.UNKNOWN].en,
        messageAr: responseData?.messageAr || errorMessages[ErrorType.UNKNOWN].ar,
        statusCode,
        originalError: error,
      };
  }
}

/**
 * الحصول على رسالة الخطأ حسب اللغة
 */
export function getErrorMessage(error: AppError, language: 'ar' | 'en' = 'en'): string {
  return language === 'ar' ? error.messageAr : error.message;
}

/**
 * عرض تنبيه الخطأ
 */
export function showErrorAlert(
  error: unknown,
  options?: {
    title?: string;
    titleAr?: string;
    onDismiss?: () => void;
    language?: 'ar' | 'en';
  },
): void {
  const appError = parseError(error);
  const isArabic = options?.language === 'ar' || I18nManager.isRTL;

  const title = isArabic ? options?.titleAr || 'خطأ' : options?.title || 'Error';

  const message = getErrorMessage(appError, isArabic ? 'ar' : 'en');

  logger.error('Error Alert:', {
    type: appError.type,
    message: appError.message,
    statusCode: appError.statusCode,
    code: appError.code,
  });

  Alert.alert(
    title,
    message,
    [
      {
        text: isArabic ? 'حسناً' : 'OK',
        onPress: options?.onDismiss,
      },
    ],
    { cancelable: true },
  );
}

/**
 * معالج الأخطاء العام
 * يمكن استخدامه في catch blocks
 */
export function handleError(
  error: unknown,
  context?: string,
  options?: {
    showAlert?: boolean;
    language?: 'ar' | 'en';
    onError?: (appError: AppError) => void;
  },
): AppError {
  const appError = parseError(error);

  // تسجيل الخطأ
  logger.error(`[${context || 'App'}] Error:`, {
    type: appError.type,
    message: appError.message,
    statusCode: appError.statusCode,
    code: appError.code,
  });

  // عرض تنبيه إذا مطلوب
  if (options?.showAlert !== false) {
    const isArabic = options?.language === 'ar' || I18nManager.isRTL;
    Alert.alert(isArabic ? 'خطأ' : 'Error', getErrorMessage(appError, isArabic ? 'ar' : 'en'), [
      { text: isArabic ? 'حسناً' : 'OK' },
    ]);
  }

  // استدعاء callback إذا موجود
  if (options?.onError) {
    options.onError(appError);
  }

  return appError;
}

/**
 * التحقق من نوع الخطأ
 */
export function isNetworkError(error: unknown): boolean {
  const appError = parseError(error);
  return appError.type === ErrorType.NETWORK || appError.type === ErrorType.TIMEOUT;
}

export function isAuthError(error: unknown): boolean {
  const appError = parseError(error);
  return appError.type === ErrorType.AUTH;
}

export function isServerError(error: unknown): boolean {
  const appError = parseError(error);
  return appError.type === ErrorType.SERVER;
}

export function isValidationError(error: unknown): boolean {
  const appError = parseError(error);
  return appError.type === ErrorType.VALIDATION;
}

/**
 * Retry wrapper للعمليات الفاشلة
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options?: {
    maxAttempts?: number;
    delayMs?: number;
    shouldRetry?: (error: AppError, attempt: number) => boolean;
    onRetry?: (error: AppError, attempt: number) => void;
  },
): Promise<T> {
  const maxAttempts = options?.maxAttempts || 3;
  const delayMs = options?.delayMs || 1000;

  let lastError: AppError | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = parseError(error);

      // تحقق هل يجب إعادة المحاولة
      const shouldRetry = options?.shouldRetry
        ? options.shouldRetry(lastError, attempt)
        : isNetworkError(error) || isServerError(error);

      if (!shouldRetry || attempt === maxAttempts) {
        throw error;
      }

      // استدعاء callback قبل إعادة المحاولة
      if (options?.onRetry) {
        options.onRetry(lastError, attempt);
      }

      logger.warn(`Retrying operation (attempt ${attempt}/${maxAttempts})...`);

      // انتظار قبل إعادة المحاولة (exponential backoff)
      await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
    }
  }

  throw lastError?.originalError || new Error('Operation failed after retries');
}

export default {
  parseError,
  getErrorMessage,
  showErrorAlert,
  handleError,
  isNetworkError,
  isAuthError,
  isServerError,
  isValidationError,
  withRetry,
  ErrorType,
};
